import { Button, Conditional, IconChevronLeft, IconChevronRight, IconSlash, Text } from '@consumidor-positivo/aurora';
import isMobile from '@libs/utils/helpers/isMobile';
import classNames from 'classnames';
import { useEffect } from 'react';

import { useCreditCarousel } from './hook';
import './styles.scss';

interface CarouselProps {
  children: React.ReactNode[];
  loading?: boolean;
  title?: string;
  showDots?: boolean;
  autoPlay?: boolean;
  autoPlayInterval?: number;
}

export const CreditCarousel: React.FC<CarouselProps> = ({
  children,
  loading,
  title = 'Item',
  showDots = false,
  autoPlay = false,
  autoPlayInterval = 10000,
}) => {
  const { carouselRef, scrollSteps, currentPage, isDragging, handleManualScroll } = useCreditCarousel({ childrenCount: children.length, loading });

  const isSinglePage = scrollSteps === 1;
  const isDesktop = !isMobile();
  const shouldShowControls = !(isDesktop && isSinglePage);

  const textDots = isMobile() ? { title } : 'Página';
  const draggingClass = classNames('credit-carousel__track', {
    'credit-carousel__track--dragging': isDragging,
  });

  useEffect(() => {
    if (!autoPlay || !showDots) return;

    const interval = setInterval(() => {
      handleManualScroll(currentPage + 1 >= scrollSteps ? -currentPage : 1);
    }, autoPlayInterval);

    return () => clearInterval(interval);
  }, [autoPlay, showDots, currentPage, scrollSteps, autoPlayInterval]);

  return (
    <div className="credit-carousel-wrapper">
      <div className="credit-carousel">
        <div className={draggingClass} ref={carouselRef}>
          {children.map((child, index) => (
            <div key={index} className="credit-carousel__item">
              {child}
            </div>
          ))}
        </div>
      </div>

      <Conditional
        condition={showDots}
        renderIf={
          <div className="credit-carousel__dots">
            {Array.from({ length: scrollSteps }).map((_, index) => (
              <span
                key={index}
                className={classNames('dot', { active: index === currentPage })}
                onClick={() => handleManualScroll(index - currentPage)}
              />
            ))}
          </div>
        }
        renderElse={
          <Conditional
            condition={shouldShowControls}
            renderIf={
              <div className="controls">
                <Button onClick={() => handleManualScroll(-1)} round type="outlined" disabled={currentPage <= 0}>
                  <Conditional condition={currentPage <= 0} renderIf={<IconSlash />} renderElse={<IconChevronLeft />} />
                </Button>

                <Text as="p" variant="body-medium" className="controls__text">
                  {typeof textDots === 'string' ? textDots : textDots.title}{' '}
                  {!!loading ? (
                    <strong>
                      <strong>...</strong>
                    </strong>
                  ) : (
                    <strong>
                      {currentPage + 1} de {scrollSteps}
                    </strong>
                  )}
                </Text>

                <Button onClick={() => handleManualScroll(1)} round type="outlined" disabled={currentPage + 1 === scrollSteps}>
                  <Conditional condition={currentPage + 1 === scrollSteps} renderIf={<IconSlash />} renderElse={<IconChevronRight />} />
                </Button>
              </div>
            }
          />
        }
      />
    </div>
  );
};
